import Modal from '@ui/Modal/component'
import ProductCard from '@ui/Product/Card/component'
import initSlider from '@ui/Slider/component'
import { isMobile } from 'assets/core/js/common'
import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import SearchMap, { MAP_MODE } from 'assets/core/js/module/searchMap'
import mapLoader from 'assets/themes/campings/js/module/mapLoader'
import type { ModalType } from '@ui/Modal/component'
import type { Calendar as SearchCalendarType } from '@ui/Search/Calendar/component'
import type { Slider } from '@ui/Slider/component'
import type { QueryResponse } from 'assets/core/js/module/searchMap'

export interface MapConfig {
  callbacks?: {
    [key in (typeof callbacksAvailable)[number]]?: ((this: void, map: SearchMapModal, ...args: unknown[]) => void)[]
  }
}

const callbacksAvailable = ['onInit', 'onLoad', 'onQuery', 'onMove', 'onMarkerClick', 'onModalOpen', 'onModalClose'] as const

export default class SearchMapModal {
  element!: HTMLElement
  config!: MapConfig
  map!: SearchMap | null
  modal!: ModalType | null
  isInit!: boolean
  isMapAvailable!: boolean
  refreshMapOnMove!: boolean
  slider!: Slider
  sliderEl!: HTMLElement | null
  init!: () => void

  constructor(element: string | HTMLElement, userConfig?: MapConfig) {
    this.element = this.resolveElement(element)

    if (!this.element?.hasAttribute('data-map-boundaries')) {
      return
    }

    const sliderEl = document.getElementById('map-products-slider')

    if (!sliderEl) {
      return
    }

    this.config = userConfig ?? {}
    this.sliderEl = sliderEl
    this.isInit = false
    this.isMapAvailable = false
    this.refreshMapOnMove = true

    this.initConfig()

    ElementPropertiesManager.addProperty<SearchMapModal>(this.element, 'searchMapModal', this)
  }

  private initConfig(): void {
    if (typeof this.config.callbacks === 'undefined') {
      this.config.callbacks = {}
    }

    callbacksAvailable.forEach((cb) => {
      if (typeof this.config.callbacks?.[cb] === 'undefined') {
        // @ts-expect-error object is known
        this.config.callbacks[cb] = []
      }
    })
  }

  private resolveElement<T extends HTMLElement>(element: string | HTMLElement): T {
    let el: HTMLElement | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector<T>(element)
    }

    if (!el || typeof el === 'string') {
      throw new Error('Missing element.')
    }

    return el as T
  }

  open(): void {
    if (!this.modal) {
      this.initModal()
    }

    this.modal?.open()
  }

  initModal(): ModalType | undefined {
    const modalMapEl = document.getElementById('search-map-modal')

    if (!modalMapEl) {
      return
    }

    this.modal = Modal(modalMapEl, {
      onOpen: () => {
        mapLoader()
          .then(() => {
            if (this.isMapAvailable === false) {
              this.initMap()
            }

            if (this.map) {
              this.map.resize()
            }
          })
          .catch(() => true)

        if (this.config.callbacks?.onModalOpen) {
          this.config.callbacks.onModalOpen.forEach((cbFn) => {
            cbFn(this)
          })
        }
      },
      onBeforeOpen: () => {
        this.sliderEl?.removeAttribute('hidden')
      },
      onBeforeClose: () => {
        this.sliderEl?.setAttribute('hidden', 'true')
      },
      onClose: () => {
        if (this.config.callbacks?.onModalClose) {
          this.config.callbacks.onModalClose.forEach((cbFn) => {
            cbFn(this)
          })
        }
      },
    })

    return this.modal
  }

  initMap(): void {
    const calendar = ElementPropertiesManager.getProperty<SearchCalendarType>(
      document.getElementById('search_offers_checkInDate') as HTMLInputElement,
      'calendar'
    )
    const checkInDateValue = calendar?.getDate()
    const modalTitleEl = this.modal?.getElement().querySelector<HTMLElement>('.o-modal__heading-title')

    if (this.config.callbacks?.onInit) {
      this.config.callbacks.onInit.forEach((cbFn) => {
        cbFn(this)
      })
    }

    this.initSlider()
    this.initToggleQueryElement()
    this.toggleLoader('map', 'visible')

    this.map = new SearchMap(
      this.element,
      {
        mode: 'query',
        onLoad: () => {
          this.toggleLoader('map', 'hidden')
          this.isMapAvailable = true

          if (this.config.callbacks?.onLoad) {
            this.config.callbacks.onLoad.forEach((cbFn) => {
              cbFn(this)
            })
          }
        },
        onQuery: (response: QueryResponse) => {
          this.updateSlider(response.tiles)

          this.toggleLoader('move', 'hidden')

          if (this.config.callbacks?.onQuery) {
            this.config.callbacks.onQuery.forEach((cbFn) => {
              cbFn(this, response)
            })
          }
        },
        onMove: () => {
          this.toggleLoader('move', 'visible')

          if (this.modal?.getElement().hasAttribute('data-move-title') && modalTitleEl) {
            modalTitleEl.innerHTML = this.modal.getElement().getAttribute('data-move-title') as string

            this.modal.getElement().removeAttribute('data-move-title')
          }

          if (this.config.callbacks?.onMove) {
            this.config.callbacks.onMove.forEach((cbFn) => {
              cbFn(this)
            })
          }
        },
        markerProperties: {
          offset: {
            bottom: [0, -55],
          },
          showIcon: (marker) => !marker.overlayText && checkInDateValue === null,
          getContent: (markerId) => {
            const sliderWrapper = this.sliderEl?.querySelector('.slider-wrapper')

            if (!sliderWrapper) {
              return
            }

            const cardEl = sliderWrapper.querySelector<HTMLElement>(`.product-card[data-id="${markerId}"]`)?.cloneNode(true) as HTMLElement

            if (!cardEl) {
              return
            }

            ProductCard(cardEl)

            return cardEl
          },
          onClick: (markerId) => {
            if (this.config.callbacks?.onMarkerClick) {
              this.config.callbacks.onMarkerClick.forEach((cbFn) => {
                cbFn(this, markerId)
              })
            }

            if (!isMobile()) {
              return true
            }

            const slideEl = this.sliderEl?.querySelector(`.slide[data-id="${markerId}"]`)

            if (!slideEl) {
              return
            }

            this.slider.slideTo(parseInt(slideEl?.getAttribute('data-index') as string, 10))

            return false
          },
        },
      },
      []
    )
  }

  private toggleLoader(type: 'map' | 'move', status: 'visible' | 'hidden'): void {
    const el = document.getElementById('search-map-loader-block')

    if (!el || !this.refreshMapOnMove) {
      return
    }

    el.setAttribute('data-loader-type', type)

    if (status === 'hidden') {
      el.setAttribute('hidden', 'hidden')
    } else if (status === 'visible') {
      el.removeAttribute('hidden')
    }
  }

  private initToggleQueryElement(): void {
    const el = document.getElementById('search-map-toggle-query')

    if (!el) {
      return
    }

    el.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement

      this.map?.setMapMode(target.checked ? MAP_MODE.QUERY : MAP_MODE.DEFAULT)
      this.refreshMapOnMove = target.checked
    })
  }

  private updateSlider(tiles: QueryResponse['tiles']): void {
    if (!this.slider) {
      setTimeout(() => {
        this.updateSlider(tiles)
      }, 250)
    } else {
      const sliderWrapper = this.sliderEl?.querySelector('.slider-wrapper')

      if (!sliderWrapper) {
        return
      }

      sliderWrapper.innerHTML = ''

      this.slider.update()

      Object.keys(tiles).forEach(function (tileId, i) {
        const slideEl = document.createElement('div')
        slideEl.className = 'slide'
        slideEl.setAttribute('data-id', tileId)
        slideEl.setAttribute('data-index', i.toString())
        slideEl.innerHTML = tiles[tileId] as string

        sliderWrapper?.appendChild(slideEl)
      })

      this.slider.update()
    }
  }

  private initSlider(): void {
    const self = this

    if (!this.sliderEl) {
      return
    }

    initSlider(this.sliderEl, {
      navigation: {
        nextEl: this.sliderEl.querySelector<HTMLElement>('.slider-button-next'),
        prevEl: this.sliderEl.querySelector<HTMLElement>('.slider-button-prev'),
      },
      spaceBetween: 10,
      lazy: false,
      speed: 1000,
      slidesPerView: 'auto',
      on: {
        init: (slider) => {
          this.slider = slider
        },
        slideChange: function (slider) {
          if (self.map) {
            self.map.openMarker(slider.getSlides()[slider.getActiveIndex()]?.getAttribute('data-id') as string)
          }
        },
        click: function (slider) {
          const url = slider.getSlides()[slider.getActiveIndex()]?.querySelector<HTMLLinkElement>('.product-card__link')?.href ?? null

          if (url) {
            window.location.href = url
          }
        },
      },
    })
  }
}
